import { useEffect } from 'react'
import { getEnv } from '@/lib/env'

type MetaTagsProps = {
  pageTitle?: string
}

/**
 * Dynamic meta tags component for white labelling
 * Injects custom meta tags based on environment variables
 */
export function MetaTags({ pageTitle }: MetaTagsProps = {}) {
  const siteName = getEnv('NEXT_PUBLIC_META_TITLE', 'Medconnect Patient')
  const title = pageTitle ? `${pageTitle} — ${siteName}` : siteName
  const description = getEnv(
    'NEXT_PUBLIC_META_DESCRIPTION',
    "Medconnect Patient's portal, book appointment, access teleconsultation, find your medical records and more."
  )
  const ogImage = getEnv('NEXT_PUBLIC_OG_IMAGE', '/logo.svg')
  const siteUrl = getEnv('NEXT_PUBLIC_SITE_URL', 'https://medconnect.com')
  const themeColor = getEnv('NEXT_PUBLIC_PRIMARY_COLOR', '#0066cc')
  const favicon = getEnv('NEXT_PUBLIC_FAVICON', '/favicon.ico')
  const appleTouchIcon = getEnv('NEXT_PUBLIC_APPLE_TOUCH_ICON', favicon)
  const icon192 = getEnv('NEXT_PUBLIC_ICON_192', favicon)
  const icon512 = getEnv('NEXT_PUBLIC_ICON_512', favicon)

  useEffect(() => {
    // Update document title
    document.title = title

    // Update meta tags
    const updateMetaTag = (property: string, content: string, attr: string = 'name') => {
      let element = document.querySelector(`meta[${attr}="${property}"]`)
      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attr, property)
        document.head.appendChild(element)
      }
      element.setAttribute('content', content)
    }

    updateMetaTag('description', description)
    updateMetaTag('og:title', title, 'property')
    updateMetaTag('og:description', description, 'property')
    updateMetaTag('og:image', ogImage, 'property')
    updateMetaTag('og:url', siteUrl, 'property')
    updateMetaTag('theme-color', themeColor)
    updateMetaTag('msapplication-navbutton-color', themeColor)

    // Helper to update link tags
    const updateLinkTag = (rel: string, href: string, sizes?: string, type?: string) => {
      let selector = `link[rel="${rel}"]`
      if (sizes) selector += `[sizes="${sizes}"]`

      let element = document.querySelector(selector) as HTMLLinkElement
      if (!element) {
        element = document.createElement('link')
        element.rel = rel
        if (sizes) element.setAttribute('sizes', sizes)
        if (type) element.setAttribute('type', type)
        document.head.appendChild(element)
      }
      element.href = href
    }

    updateLinkTag('icon', favicon)
    updateLinkTag('apple-touch-icon', appleTouchIcon)
    updateLinkTag('icon', icon192, '192x192', 'image/png')
    updateLinkTag('icon', icon512, '512x512', 'image/png')
    updateLinkTag('canonical', siteUrl)
  }, [title, description, ogImage, siteUrl, themeColor, favicon, appleTouchIcon, icon192, icon512])

  return null
}
